import {
  RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import { createPortal } from 'react-dom';
import { QRCodeCanvas } from 'qrcode.react';

import Icon from '@components/icon/Icon';
import Tooltip from '@components/tooltip/Tooltip';
import testId from '@constants/testId';
import { Event, sendMixpanelEvent } from '@core/analytics/mixpanel';
import { ActionsProps } from '@features/transactions/types';
import { downloadQRCode } from '@utils/downloadFile';

import theme from '../../../themes/colors';

interface ThreeDotActionButtonProps {
  id: string;
  actions?: ActionsProps[];
  isDisabled?: boolean;
  onToggle?: (isOpen: boolean) => void;
  source?: string;
  stroke?: string;
  iconPosition?: 'left' | 'right';
  modalStyle?: string;
  usePortal?: boolean;
}

interface DropdownPosition {
  top: number;
  left: number;
}

const disableButtonStyle = '!text-grey opacity-50 cursor-not-allowed';
const DROPDOWN_WIDTH = 240;
const DROPDOWN_GAP = 4;

const ThreeDotActionButton = ({
  id,
  actions,
  isDisabled,
  onToggle,
  source,
  stroke = theme.grey,
  iconPosition = 'right',
  modalStyle = '',
  usePortal = false
}: ThreeDotActionButtonProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLDivElement>(null);

  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [tooltipText, setTooltipText] = useState<string>('');
  const [hoveredActionId, setHoveredActionId] = useState<string>('');
  const [dropdownPosition, setDropdownPosition] =
    useState<DropdownPosition | null>(null);
  const [openAbove, setOpenAbove] = useState<boolean>(false);

  const updatePosition = useCallback(() => {
    if (!triggerRef.current || !dropdownRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const dropdownRect = dropdownRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    const menuHeight = dropdownRect.height || (actions?.length || 1) * 48 + 16;
    const spaceBelow = viewportHeight - triggerRect.bottom - DROPDOWN_GAP;
    const spaceAbove = triggerRect.top - DROPDOWN_GAP;

    const shouldOpenAbove = spaceBelow < menuHeight && spaceAbove >= menuHeight;
    setOpenAbove(shouldOpenAbove);

    if (!usePortal) return;

    let left = triggerRect.right - DROPDOWN_WIDTH;
    if (left < 8) left = 8;
    if (left + DROPDOWN_WIDTH > viewportWidth - 8) {
      left = viewportWidth - DROPDOWN_WIDTH - 8;
    }

    const top = shouldOpenAbove
      ? triggerRect.top - menuHeight - DROPDOWN_GAP
      : triggerRect.bottom + DROPDOWN_GAP;

    setDropdownPosition({ top, left });
  }, [actions?.length, usePortal]);

  const handleClick = (event: React.MouseEvent) => {
    if (isDisabled) return;
    event.stopPropagation();

    if (modalOpen) {
      setModalOpen(false);
      setDropdownPosition(null);
      return;
    }

    setModalOpen(true);
  };

  useLayoutEffect(() => {
    if (modalOpen) {
      updatePosition();
    }
  }, [modalOpen, updatePosition]);

  const handleClose = useCallback(() => {
    setModalOpen(false);
    setDropdownPosition(null);
  }, []);

  useEffect(() => {
    if (!modalOpen) return undefined;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const isContainerClick = containerRef.current?.contains(target);
      const isDropdownClick = dropdownRef.current?.contains(target);

      if (!isContainerClick && !isDropdownClick) {
        handleClose();
      }
    };

    const handleScroll = () => {
      if (usePortal) handleClose();
    };

    document.addEventListener('mousedown', handleClickOutside);
    if (usePortal) {
      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', updatePosition);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (usePortal) {
        window.removeEventListener('scroll', handleScroll, true);
        window.removeEventListener('resize', updatePosition);
      }
    };
  }, [modalOpen, handleClose, updatePosition, usePortal]);

  useEffect(() => {
    onToggle?.(modalOpen);
  }, [onToggle, modalOpen]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (tooltipText !== '') setTooltipText('');
    }, 1000);

    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [tooltipText]);

  const dropdownContent = modalOpen && (
    <div
      ref={dropdownRef}
      className={`${
        usePortal ? 'fixed' : 'absolute right-0'
      } z-[9999] w-60 rounded-lg bg-white shadow-md ${
        !usePortal && openAbove ? 'bottom-full mb-1' : ''
      } ${!usePortal && !openAbove ? 'top-full mt-1' : ''} ${modalStyle}`}
      style={
        usePortal
          ? {
              top: dropdownPosition?.top ?? -9999,
              left: dropdownPosition?.left ?? -9999,
              visibility: dropdownPosition ? 'visible' : 'hidden'
            }
          : undefined
      }
    >
      {actions?.map((action, index) => {
        if (action.onClick || action.qrLink)
          return (
            <div key={action.id} className="relative">
              <div
                role="presentation"
                className={`relative flex cursor-pointer items-center gap-2 p-3 ${
                  !action.isDisabled ? 'hover:bg-gray-50' : ''
                } ${index === 0 ? 'rounded-t-lg' : 'border-t border-grey40'} ${
                  action.isDisabled ? `${disableButtonStyle}` : ''
                } ${action.style ?? ''} ${
                  index + 1 !== actions.length ? '' : 'rounded-b-lg'
                }`}
                onClick={(event) => {
                  if (action.isDisabled) {
                    event.preventDefault();
                    event.stopPropagation();
                    return;
                  }
                  if (action.downloadQrCode) {
                    if (source)
                      sendMixpanelEvent(Event.DOWNLOAD_QR_CODE_IN_EVENTS_CARD);
                    event.stopPropagation();
                    downloadQRCode(
                      canvasRef as RefObject<HTMLDivElement>,
                      'qr code'
                    );
                  }
                  action.onClick?.(event);
                  if (action.tooltipText) {
                    setTooltipText(action.tooltipText);
                    setTimeout(() => {
                      handleClose();
                    }, 1000);
                  } else {
                    handleClose();
                  }
                }}
                onMouseEnter={() => setHoveredActionId(action.id || '')}
                onMouseLeave={() => setHoveredActionId('')}
                data-tooltip-id={
                  action.isDisabled && action.disabledTooltip
                    ? `disabled-tooltip-${action.id}`
                    : `copy-tooltip-${action.id}`
                }
              >
                {iconPosition === 'left' && action.iconName && (
                  <Icon
                    name={action.iconName}
                    stroke={theme.primary}
                    className={action.isDisabled ? 'opacity-50' : ''}
                  />
                )}
                {action.label}
                {iconPosition === 'right' && action.iconName && (
                  <Icon
                    name={action.iconName}
                    stroke={theme.primary}
                    className={action.isDisabled ? 'opacity-50' : ''}
                  />
                )}
                {tooltipText && action.tooltipText && (
                  <Tooltip
                    id={`copy-tooltip-${action.id}`}
                    type="text"
                    showTooltip={tooltipText !== ''}
                    tooltipText="Copied!"
                  />
                )}
                {action.qrLink && (
                  <div ref={canvasRef} className="hidden">
                    <QRCodeCanvas
                      value={action.qrLink}
                      bgColor="white"
                      fgColor="black"
                      marginSize={4}
                    />
                  </div>
                )}
              </div>
              {action.isDisabled &&
                action.disabledTooltip &&
                hoveredActionId === action.id &&
                (action.showWarningIcon ? (
                  <Tooltip
                    id={`disabled-tooltip-${action.id}`}
                    type="custom"
                    showTooltip
                    variant="dark"
                    place="bottom"
                    className="!max-w-90"
                  >
                    <div className="flex items-start gap-2 p-1">
                      <Icon
                        name="alert-circle-red"
                        size="medium"
                        className="shrink-0"
                      />
                      <span className="text-sm font-normal">
                        {action.disabledTooltip}
                      </span>
                    </div>
                  </Tooltip>
                ) : (
                  <Tooltip
                    id={`disabled-tooltip-${action.id}`}
                    type="text"
                    showTooltip
                    tooltipText={action.disabledTooltip}
                    variant="dark"
                    place="bottom"
                    className="!italic"
                  />
                ))}
            </div>
          );

        return null;
      })}
    </div>
  );

  return (
    <div
      className="relative max-w-8"
      ref={containerRef}
      data-testid={testId.transactionsPage.overflowMenu}
    >
      <div
        ref={triggerRef}
        role="presentation"
        key={id}
        onClick={(event) => handleClick(event)}
        className={`p-2 ${
          isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'
        }`}
      >
        <Icon
          name="three-dots-group"
          stroke={stroke}
          className={isDisabled ? 'opacity-30' : ''}
        />
      </div>

      {usePortal
        ? createPortal(dropdownContent, document.body)
        : dropdownContent}
    </div>
  );
};

export default ThreeDotActionButton;
